import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  hasServiceAccessAtLeast,
  roleHasMatrixPermission,
  type Permission,
  type ServiceAccessLevel,
  type ServiceDomain,
} from "src/lib/roles";
import {
  selectAuthInitialized,
  selectAuthLoading,
  selectUserProfile,
} from "src/store/authSlice";
import RocketLaunchLoader from "./RocketLaunchLoader";

interface RoleGuardProps {
  children: React.ReactNode;
  permission?: Permission;
  anyPermissions?: Permission[];
  /** Require at least this level on a CMS service domain (read ⊂ write ⊂ manage). */
  service?: ServiceDomain;
  minServiceLevel?: ServiceAccessLevel;
  fallbackPath?: string;
}

const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  permission,
  anyPermissions,
  service,
  minServiceLevel = "read",
  fallbackPath = "/",
}) => {
  const userProfile = useSelector(selectUserProfile);
  const initialized = useSelector(selectAuthInitialized);
  const loading = useSelector(selectAuthLoading);

  if (!initialized || loading) {
    return <RocketLaunchLoader message="Loading permissions…" />;
  }

  if (!userProfile) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (permission && !roleHasMatrixPermission(userProfile.role, permission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (
    anyPermissions &&
    !anyPermissions.some((p) => roleHasMatrixPermission(userProfile.role, p))
  ) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (
    service &&
    !hasServiceAccessAtLeast(userProfile.role, service, minServiceLevel)
  ) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (!userProfile.isActive) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-indigo-900 via-gray-900 to-black text-white">
        <div className="text-center max-w-md px-6">
          <h1 className="text-4xl font-bold mb-4">Account Inactive</h1>
          <p className="text-gray-400">
            Your account has been deactivated. Please contact an administrator
            for assistance.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleGuard;
